import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowRight } from 'lucide-react';
import useInView from '../hooks/useInView';
import CoreFeaturesSection from '../sections/CoreFeaturesSection';
import HowItWorksSection from '../sections/HowItWorksSection';

const RONI = "/images/home/roni-feature.png";

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);
  return (
    <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">{children}</p>
);

const Features = () => (
  <main className="bg-[#05050A] min-h-screen">
    {/* Hero */}
    <section className="relative pt-32 pb-20 overflow-hidden">
      <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-purple-600/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionLabel>Platform Features</SectionLabel>
        <h1 className="font-outfit font-black text-5xl sm:text-6xl text-white mb-6 tracking-tight leading-[1.05] max-w-3xl">
          A creative platform inside.<br />
          <span className="gradient-text">A safety architecture around it.</span>
        </h1>
        <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-2xl">
          Ten interactive feature deep-dives. Tap any card to open a full Feature Report — including child benefit, parent-trust benefit, safety notes, and how it connects to the rest of the platform.
        </p>
      </div>
    </section>

    {/* Interactive feature explorer */}
    <CoreFeaturesSection
      heading="Core Features"
      subtitle="Explore the systems that make ROMIND KIDS a safer creative universe for children aged 9–14."
    />

    {/* Journey */}
    <section className="bg-[#080714]">
      <HowItWorksSection />
    </section>

    {/* RONI spotlight */}
    <section data-testid="roni-spotlight" className="py-16 bg-[#080714]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="glass-card p-8 sm:p-12 border border-cyan-500/15">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <SectionLabel>AI Safety Companion</SectionLabel>
              <h2 className="font-outfit font-bold text-3xl text-white mb-4 tracking-tight">Meet RONI.</h2>
              <p className="text-white/55 text-sm leading-relaxed mb-6">
                RONI is the AI-assisted safety companion built into the heart of ROMIND KIDS. Not a surveillance tool — a warm, helpful presence that supports children, especially when they are new, alone, or unsure.
              </p>
              <div className="space-y-3">
                {[
                  "Detects unsafe wording and risky interaction patterns",
                  "Offers in-context guidance, not silent monitoring",
                  "Acts as a friendly companion for new or quiet users",
                  "Connects directly to reporting and blocking flows",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <ShieldCheck size={14} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                    <span className="text-white/55 text-xs">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 scale-125 bg-cyan-500/25 rounded-full blur-[80px]" />
                <img
                  src={RONI}
                  alt="RONI Safety Companion"
                  className="relative w-64 sm:w-72 lg:w-80 animate-float"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <FadeIn>
          <Link to="/child-safety" data-testid="features-safety-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-purple-600/80 border border-purple-400/40 text-white hover:bg-purple-500 transition-all">
            Read Our Safety Philosophy <ArrowRight size={14} />
          </Link>
        </FadeIn>
      </div>
    </section>
  </main>
);

export default Features;
